import { useState } from "react";
import DayCalendar from "@/components/DayCalendar";
import MonthCalendar from "@/components/MonthCalendar";
import WeekCalendar from "@/components/WeekCalendar";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
} from "lucide-react";
import {
  format,
  addDays,
  subDays,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  startOfWeek,
} from "date-fns";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { MOVE_COLORS, LEGEND_TYPES } from "@/constants";

function CalendarPage({ onDate = new Date() }: { onDate?: Date }) {
  const [view, setView] = useState<"day" | "week" | "month">("week");
  const [currentDate, setCurrentDate] = useState(onDate);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);

  // Handlers for navigation
  const handlePrev = () => {
    setCurrentDate((prev) =>
      view === "day"
        ? subDays(prev, 1)
        : view === "week"
        ? subWeeks(prev, 1)
        : subMonths(prev, 1)
    );
  };
  const handleNext = () => {
    setCurrentDate((prev) =>
      view === "day"
        ? addDays(prev, 1)
        : view === "week"
        ? addWeeks(prev, 1)
        : addMonths(prev, 1)
    );
  };
  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const currentDisplay =
    view === "day"
      ? format(currentDate, "EEEE, MMMM d, yyyy")
      : view === "week"
      ? `${format(
          startOfWeek(currentDate, { weekStartsOn: 1 }),
          "MMM d"
        )} - ${format(
          addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), 6),
          "MMM d, yyyy"
        )}`
      : format(currentDate, "MMMM yyyy");

  return (
    <div className="p-6">
      <div className="flex flex-col gap-2">
        {/* Page Title */}
        <div className="flex items-center grid grid-cols-2 md:grid-cols-3">
          <h1 className="text-2xl font-bold col-span-1">Calendar</h1>
          <h2 className="text-xl font-bold col-span-1 text-right md:text-center">
            {currentDisplay}
          </h2>
          <div className="col-span-1">&nbsp;</div>
        </div>

        {/* Navigation Bar */}
        <div className="flex items-center justify-between w-full mt-2">
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrev}
              className="px-2 py-1 h-8 pl-1 bg-white border rounded-md hover:bg-gray-50 flex items-center gap-0.5 justify-between"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Prev</span>
            </button>
            <button
              onClick={handleNext}
              className="px-2 py-1 h-8 pr-1 bg-white border rounded-md hover:bg-gray-50 flex items-center gap-0.5 justify-between"
            >
              <span className="text-sm font-medium">Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
            {view === "day" ? (
              <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={
                      "px-2 py-1 h-8 bg-white border rounded-md flex items-center gap-2 font-normal w-[130px]"
                    }
                  >
                    {format(currentDate, "MM/dd/yyyy")}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={currentDate}
                    onSelect={(date) => {
                      setCurrentDate(date || new Date());
                      setDatePopoverOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <button
                onClick={handleToday}
                className="px-2 py-1 h-8 border rounded-md hover:bg-gray-50 flex items-center gap-2 bg-sky-50"
              >
                <span className="text-sm font-medium">Today</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm">
            <button
              disabled={view === "day"}
              onClick={() => setView("day")}
              className={`px-2 py-1 bg-white border rounded-md hover:bg-gray-50 font-medium h-8 ${
                view === "day" ? "bg-gray-50" : ""
              }`}
            >
              Day
            </button>
            <button
              disabled={view === "week"}
              onClick={() => setView("week")}
              className={`px-2 py-1 bg-white border rounded-md hover:bg-gray-50 font-medium h-8 ${
                view === "week" ? "bg-gray-50" : ""
              }`}
            >
              Week
            </button>
            <button
              disabled={view === "month"}
              onClick={() => setView("month")}
              className={`px-2 py-1 bg-white border rounded-md hover:bg-gray-50 font-medium h-8 ${
                view === "month" ? "bg-gray-50" : ""
              }`}
            >
              Month
            </button>
          </div>
        </div>
      </div>

      <div className="my-8 border border-stone-500">
        {view === "day" ? (
          <DayCalendar onDate={currentDate} dispatchMode={true} />
        ) : view === "week" ? (
          <WeekCalendar currentDate={currentDate} />
        ) : (
          <MonthCalendar currentDate={currentDate} />
        )}
      </div>

      {/* Legend */}
      <div className="mt-24 fixed left-0 bottom-0 w-screen px-6 py-4 mb-0 z-10 flex flex-row flex-wrap items-center gap-2 md:gap-2 text-sm flex-wrap bg-white">
        {LEGEND_TYPES.map((moveType) => (
          <div className="flex flex-row items-center gap-2" key={moveType.key}>
            <div
              className={
                "w-8 h-4 rounded " +
                (MOVE_COLORS[moveType.key as keyof typeof MOVE_COLORS] ||
                  "bg-gray-300")
              }
            ></div>
            <div>{moveType.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CalendarPage;
